import { Card, Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { ButtonLink, LoadingButton } from '../../../components';
import { useCallback, useEffect, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';
import { useSelector, useDispatch } from 'react-redux';
import { Technologies, UserProfiles } from '../../../services';
import { Profile } from '../../../services/Contexture/Profile';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import { EDIT_PROFILE_VALIDATION } from '../../../utils/validationSchemas';
import { setApplicationProfile, setUserProfile } from '../../../redux/slices/profilesSlice';
import {
  fetchTechnologiesIfNeeded,
  invalidate,
} from '../../../redux/slices/technologiesSlice';
import {
  selectStackOptions,
  selectToolOptions,
  selectTechnologiesState,
} from '../../../redux/selectors/technologiesSelectors';
import {
  selectAuthUser,
  selectAuthProfiles,
} from '../../../redux/selectors/authSelectors';
import {
  selectUserProfile,
  selectApplicationProfile,
} from '../../../redux/selectors/profileSelectors';
import { CreatableTechToolsSelect } from '../../../components/CreatableTechToolsSelect';

const StyledCard = styled(Card)`
  padding: 0 30px;
`;

const StyledTitle = styled(Card.Title)`
  align-items: center;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
`;

const StyledLabel = styled.span`
  ${({ required }) => {
    return `
    color: #A3A5A9;
    display: block;

    ${required
        ? `
      &:after {
        content: '*';
        display: inline-block;
        color: rgb(158, 13, 61);
        font-weight: 700;
        margin-left: 5px;
      }
    `
        : ''
      }
  `;
  }}
`;

const StyledNav = styled.nav`
  display: flex;
  border-top: 1px solid #e9ecef;
`;

const StyledNavItem = styled(Link)`
  ${({ active }) => {
    return `
    border-bottom: 2px solid transparent;
    border-color: ${active ? '#fcb12e' : 'transparent'};
    color: #4D5C6D !important;
    display: block;
    font-size: .875rem;
    text-decoration: none !important;
    padding: 15px 30px;
    transition: all .1s ease-out;

    &:hover {
      color: #fcb12e !important;
    }
  `;
  }}
`;

const StyledVisibilityToggle = styled.span`
  font-size: 0.75rem;
  color: #a3a5a9;
`;

const StyledInput = styled.input`
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px 12px;
  width: 100%;
  font-size: 0.875rem;
  color: #4d5c6d;

  &:focus {
    outline: none;
    border-color: #fcb12e;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FieldError = ({ message }) => {
  if (!message) return null;

  return <div className='text-danger mt-1'>{message}</div>;
};

const selectStyles = {
  control: (styles) => ({ ...styles, backgroundColor: 'white' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: '#fff',
      color: isDisabled ? '#ccc' : isSelected ? '#fcb12e' : '#4D5C6D',
      ':hover': {
        color: '#fcb12e',
        cursor: 'pointer',
      },
      ':focus': {
        color: '#fcb12e',
        cursor: 'pointer',
      },
      ':active': {
        color: '#fcb12e',
        cursor: 'pointer',
      },
    };
  },
  multiValue: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: 'rgb(158, 13, 61)',
      color: '#fff',
      fontWeight: 700,
    };
  },
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: data.color,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: data.color,
  }),
};

export const EditUserProfile = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const profiles = useSelector(selectAuthProfiles);
  const userProfile = useSelector(selectUserProfile);
  const applicationProfile = useSelector(selectApplicationProfile);
  const technologiesState = useSelector(selectTechnologiesState);
  const techStackOptions = useSelector(selectStackOptions);
  const toolsOptions = useSelector(selectToolOptions);
  const auth = useAuth();
  const [selectedTechStack, setSelectedTechStack] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [isLoadingTechStack, setIsLoadingTechStack] = useState(false);
  const [isLoadingTools, setIsLoadingTools] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // State for editable profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nickname, setNickname] = useState('');
  const [industry, setIndustry] = useState('');
  const [team, setTeam] = useState('');
  const [jobFunction, setJobFunction] = useState('');

  const navigate = useNavigate();

  const validateProfile = useCallback(
    async (overrideValues) => {
      const techStackValues = selectedTechStack.map((item) => item.value);
      const toolsValues = selectedTools.map((item) => item.value);

      const valuesToValidate = {
        firstName,
        lastName,
        techStack: techStackValues,
        tools: toolsValues,
        ...overrideValues,
      };

      try {
        await EDIT_PROFILE_VALIDATION.validate(valuesToValidate, {
          abortEarly: false,
        });
        setErrors({});
        return true;
      } catch (validationError) {
        if (validationError && validationError.inner) {
          const formErrors = {};

          validationError.inner.forEach((err) => {
            if (err.path && !formErrors[err.path]) {
              formErrors[err.path] = err.message;
            }
          });

          setErrors(formErrors);
        }

        return false;
      }
    },
    [firstName, lastName, selectedTechStack, selectedTools]
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // Fetch technologies via Redux when component mounts
  useEffect(() => {
    if (!user?.repositoryId) {
      return;
    }

    dispatch(fetchTechnologiesIfNeeded(user.repositoryId));
  }, [dispatch, user, user?.repositoryId]);

  // Mirror Redux loading state into local loading flags for UI
  useEffect(() => {
    setIsLoadingTechStack(technologiesState.isFetching);
    setIsLoadingTools(technologiesState.isFetching);
  }, [technologiesState.isFetching]);

  const saveTechStackTerms = useCallback(
    async (terms) => {
      if (!profiles || !Array.isArray(terms)) return;

      const loadingToastId = toast.loading('Updating technologies...', {
        position: 'top-right',
      });

      try {
        const profileKeys = ['stack_news', 'stack_cve', 'stack_podcasts'];

        for (const key of profileKeys) {
          const profile = profiles[key];
          if (!profile?.profileId) continue;

          await Profile.createOrUpdateCustomTerms(
            { terms },
            { profileId: profile.profileId }
          );
        }
        // Update Redux userProfile with new techStack
        const updatedUserProfile = {
          ...userProfile,
          techStack: terms?.join(','),
        };
        dispatch(setUserProfile(updatedUserProfile));


        toast.dismiss(loadingToastId);
        toast.success('Technologies updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
      } catch (error) {
        toast.dismiss(loadingToastId);
        toast.error('Error updating technologies', {
          position: 'top-right',
          autoClose: 4000,
        });
      }
    },
    [dispatch, profiles, userProfile]
  );

  const saveToolsTerms = useCallback(
    async (terms) => {
      if (!profiles || !Array.isArray(terms)) return;

      const loadingToastId = toast.loading('Updating tools...', {
        position: 'top-right',
      });

      try {
        const profileKeys = ['tools_news', 'tools_cve', 'tools_podcasts'];

        for (const key of profileKeys) {
          const profile = profiles[key];
          if (!profile?.profileId) continue;

          await Profile.createOrUpdateCustomTerms(
            { terms },
            { profileId: profile.profileId }
          );
        }

        // Update Redux userProfile with new tools
        const updatedUserProfile = {
          ...userProfile,
          tools: terms?.join(','),
        };
        dispatch(setUserProfile(updatedUserProfile));

        toast.dismiss(loadingToastId);
        toast.success('Tools updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
      } catch (error) {
        toast.dismiss(loadingToastId);
        toast.error('Error updating tools', {
          position: 'top-right',
          autoClose: 4000,
        });
      }
    },
    [dispatch, profiles, userProfile]
  );

  // Save function - validates all fields, saves only detail fields (not tech stack/tools)
  const handleSave = async () => {
    const isValid = await validateProfile();
    if (!isValid) return;

    if (!auth.user?.profile?.['cognito:username']) {
      return;
    }

    const userId = auth.user.profile?.['cognito:username'];
    const contextureEmailAddress =
      auth.user?.profile?.email ||
      user?.emailAddress ||
      user?.email ||
      undefined;

    try {
      setIsSaving(true);

      // Prepare update data with profile fields (excluding tools, techStack, profiles, team)
      const updateData = {
        userId: userId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        nickname: nickname.trim(),
        industry: industry.trim(),
        // team is disabled and should not be updated
        jobFunction: jobFunction.trim(),
        contextureEmailAddress,
      };

      // Remove undefined values
      Object.keys(updateData).forEach(
        (key) => updateData[key] === undefined && delete updateData[key]
      );

      // Update user profile in database (only core fields, not tech/tools)
      if (Object.keys(updateData).length > 1) {
        // Only update if there's data besides userId
        await UserProfiles.update(userId, updateData);

        // Update Redux store with the new profile data
        // Merge updated fields with existing applicationProfile
        const updatedApplicationProfile = {
          ...applicationProfile,
          ...updateData,
        };
        dispatch(setApplicationProfile(updatedApplicationProfile));
      }

      toast.success(`Profile updated successfully!`, {
        position: 'top-right',
        autoClose: 4000,
      });

      navigate('/profile');
    } catch (error) {
      toast.error(`Error saving profile`, {
        position: 'top-right',
        autoClose: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Initialize selected values when user profile loads
  useEffect(() => {
    if (userProfile) {
      // Parse techStack and tools from comma-separated strings
      const techStackArray = userProfile.techStack
        ? userProfile.techStack
          .split(',')
          .map((tech) => tech.trim())
          .filter((tech) => tech)
        : [];
      const toolsArray = userProfile.tools
        ? userProfile.tools
          .split(',')
          .map((tool) => tool.trim())
          .filter((tool) => tool)
        : [];

      setSelectedTechStack(
        techStackArray.map((tech) => ({ value: tech, label: tech }))
      );
      setSelectedTools(
        toolsArray.map((tool) => ({ value: tool, label: tool }))
      );
    }
  }, [userProfile]);

  // Initialize profile fields from applicationProfile
  useEffect(() => {
    // Always set team from user object (as it's disabled and shouldn't come from applicationProfile)
    if (user) {
      setTeam(user.team || user.teamName || '');
    }

    // All other fields come from applicationProfile only (except tech stack and tools)
    if (applicationProfile) {
      setFirstName(applicationProfile.firstName ?? '');
      setLastName(applicationProfile.lastName ?? '');
      setNickname(applicationProfile.nickname ?? '');
      setIndustry(applicationProfile.industry ?? '');
      setJobFunction(applicationProfile.jobFunction ?? '');
    }
  }, [applicationProfile, user]);

  // Return loading state if user data is not yet available
  if (!user) {
    return <div>Loading user profile...</div>;
  }

  return (
    <div className='my-4'>
      <Row className='my-4'>
        <Col lg={11} className='flex justify-content-start mt-2'>
          <ButtonLink
            to='/profile'
            className='flex align-items-center p-0'
            variant='minimal'
          >
            <FaArrowLeft /> <div className='px-2'>Back to View Profile</div>
          </ButtonLink>
        </Col>
      </Row>
      <Row>
        <Col lg={11}>
          <Card>
            <Row>
              <Col>
                <h3 className='p-4 m-0'>Edit Profile</h3>

                {/* Hidden for now since there is only one tab
                 <StyledNav>
                  <StyledNavItem active to='/'>
                    Profile details
                  </StyledNavItem> */}
                {/* <StyledNavItem to='/'>Profile Photo</StyledNavItem> */}
                {/* <StyledNavItem to='/'>Cover Photo</StyledNavItem> */}
                {/* </StyledNav> */}
              </Col>
            </Row>
          </Card>

          <StyledCard className='mt-3'>
            <StyledTitle>Technologies</StyledTitle>

            <div className='mt-2 mb-3'>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel required>Tech Stack</StyledLabel>
                </Col>
                <Col lg={10}>
                  <p className='mb-2'>
                    What technologies do you monitor within your practice?
                  </p>

                  <CreatableTechToolsSelect
                    isMulti
                    placeholder={
                      isLoadingTechStack
                        ? 'Loading technologies...'
                        : 'Select technologies or create new ones'
                    }
                    value={selectedTechStack}
                    options={techStackOptions}
                    styles={selectStyles}
                    isLoading={isLoadingTechStack}
                    onCreateOption={async (inputValue) => {
                      const content = (inputValue || '').trim();
                      if (!content) return;

                      const userId = auth.user?.profile['cognito:username'];

                      try {
                        if (userId) {
                          await Technologies.create({
                            name: content,
                            tech_id: uuidv4(),
                            type: 'stack',
                            user_id: userId,
                          });
                          if (user?.repositoryId) {
                            dispatch(invalidate());
                            dispatch(
                              fetchTechnologiesIfNeeded(user.repositoryId)
                            );
                          }
                        }
                      } catch (error) {
                        toast.error('Failed to create new technology');
                        return;
                      }

                      const updated = [
                        ...selectedTechStack,
                        { label: content, value: content },
                      ];
                      setSelectedTechStack(updated);
                      const values = updated.map((item) => item.value);
                      await validateProfile({
                        techStack: values,
                      });
                      await saveTechStackTerms(values);
                    }}
                    onChange={async (selectedOptions) => {
                      const updated = selectedOptions || [];
                      if (!updated.length) {
                        toast.warn(
                          'At least one technology must remain selected.',
                          {
                            position: 'top-right',
                            autoClose: 3000,
                          }
                        );
                        return;
                      }
                      setSelectedTechStack(updated);
                      const values = updated.map((item) => item.value);
                      await validateProfile({
                        techStack: values,
                      });
                      await saveTechStackTerms(values);
                    }}
                    data-testid='tech-stack-dropdown'
                    noOptionsMessage={() =>
                      'No technologies found. Type to create a new one.'
                    }
                    formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    getUserId={() => auth.user?.profile['cognito:username']}
                    onDeleteOption={async (opt) => {
                      if (!opt.techId) return;

                      try {
                        // Prevent deleting the last remaining technology
                        if (selectedTechStack.length <= 1) {
                          toast.warn(
                            'At least one technology must remain selected.',
                            {
                              position: 'top-right',
                              autoClose: 3000,
                            }
                          );
                          return;
                        }

                        const response = await Technologies.delete(opt.techId, {
                          profileId: user.repositoryId,
                        });

                        if (user?.repositoryId && response?.success) {
                          dispatch(invalidate());
                          dispatch(
                            fetchTechnologiesIfNeeded(user.repositoryId)
                          );
                        }

                        const remaining = selectedTechStack.filter(
                          (item) => item.value !== opt.value
                        );
                        setSelectedTechStack(remaining);

                        await saveTechStackTerms(
                          remaining.map((item) => item.value)
                        );
                      } catch (error) {
                        toast.error('Failed to delete tech stack');
                      }
                    }}
                  />
                  <FieldError message={errors.techStack} />

                  <StyledVisibilityToggle>Public</StyledVisibilityToggle>
                </Col>
              </Row>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel required>Tools</StyledLabel>
                </Col>
                <Col lg={10}>
                  <p className='mb-2'>
                    What tools do you use in your practice?
                  </p>

                  <CreatableTechToolsSelect
                    isMulti
                    styles={selectStyles}
                    placeholder={
                      isLoadingTools
                        ? 'Loading tools...'
                        : 'Select tools or create new ones'
                    }
                    value={selectedTools}
                    options={toolsOptions}
                    isLoading={isLoadingTools}
                    onCreateOption={async (inputValue) => {
                      const content = (inputValue || '').trim();
                      if (!content) return;

                      const userId = auth.user?.profile['cognito:username'];

                      try {
                        if (userId) {
                          await Technologies.create({
                            name: content,
                            tech_id: uuidv4(),
                            type: 'tool',
                            user_id: userId,
                          });
                          if (user?.repositoryId) {
                            dispatch(invalidate());
                            dispatch(
                              fetchTechnologiesIfNeeded(user.repositoryId)
                            );
                          }
                        }
                      } catch (error) {
                        toast.error('Failed to create new tool');
                        return;
                      }

                      const updated = [
                        ...selectedTools,
                        { label: content, value: content },
                      ];
                      setSelectedTools(updated);
                      const values = updated.map((item) => item.value);
                      await validateProfile({
                        tools: values,
                      });
                      await saveToolsTerms(values);
                    }}
                    onChange={async (selectedOptions) => {
                      const updated = selectedOptions || [];
                      if (!updated.length) {
                        toast.warn(
                          'At least one tool must remain selected.',
                          {
                            position: 'top-right',
                            autoClose: 3000,
                          }
                        );
                        return;
                      }
                      setSelectedTools(updated);
                      const values = updated.map((item) => item.value);
                      await validateProfile({
                        tools: values,
                      });
                      await saveToolsTerms(values);
                    }}
                    data-testid='tools-dropdown'
                    noOptionsMessage={() =>
                      'No tools found. Type to create a new one.'
                    }
                    formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    getUserId={() => auth.user?.profile['cognito:username']}
                    onDeleteOption={async (opt) => {
                      if (!opt.techId) return;

                      try {
                        // Prevent deleting the last remaining tool
                        if (selectedTools.length <= 1) {
                          toast.warn(
                            'At least one tool must remain selected.',
                            {
                              position: 'top-right',
                              autoClose: 3000,
                            }
                          );
                          return;
                        }

                        const response = await Technologies.delete(opt.techId, {
                          profileId: user.repositoryId,
                        });

                        if (user?.repositoryId && response?.success) {
                          dispatch(invalidate());
                          dispatch(
                            fetchTechnologiesIfNeeded(user.repositoryId)
                          );
                        }

                        const remaining = selectedTools.filter(
                          (item) => item.value !== opt.value
                        );
                        setSelectedTools(remaining);

                        await saveToolsTerms(
                          remaining.map((item) => item.value)
                        );
                      } catch (error) {
                        toast.error('Failed to delete tool');
                      }
                    }}
                  />
                  {errors.tools && (
                    <div className='text-danger mt-1'>{errors.tools}</div>
                  )}

                  <StyledVisibilityToggle>Public</StyledVisibilityToggle>
                </Col>
              </Row>
            </div>
          </StyledCard>

          <StyledCard className='my-3'>
            <StyledTitle>Details</StyledTitle>

            <div className='mt-2 mb-3'>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel required>First Name</StyledLabel>
                </Col>
                <Col lg={10}>
                  <StyledInput
                    autoFocus
                    value={firstName}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setFirstName(value);
                      await validateProfile({ firstName: value });
                    }}
                  />
                  <FieldError message={errors.firstName} />
                  <StyledVisibilityToggle>Public</StyledVisibilityToggle>
                </Col>
              </Row>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel required>Last Name</StyledLabel>
                </Col>
                <Col lg={10}>
                  <StyledInput
                    value={lastName}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setLastName(value);
                      await validateProfile({ lastName: value });
                    }}
                  />
                  <FieldError message={errors.lastName} />
                  <StyledVisibilityToggle>Public</StyledVisibilityToggle>
                </Col>
              </Row>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel>Nickname</StyledLabel>
                </Col>
                <Col lg={10}>
                  <StyledInput
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                  />
                  <StyledVisibilityToggle>Public</StyledVisibilityToggle>
                </Col>
              </Row>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel>Industry</StyledLabel>
                </Col>
                <Col lg={10}>
                  <StyledInput
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                  />
                  <StyledVisibilityToggle>Only Me</StyledVisibilityToggle>
                </Col>
              </Row>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel>Team</StyledLabel>
                </Col>
                <Col lg={10}>
                  <StyledInput
                    value={team}
                    onChange={(e) => setTeam(e.target.value)}
                    disabled
                  />
                  <StyledVisibilityToggle>Only Me</StyledVisibilityToggle>
                </Col>
              </Row>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel>Job Function</StyledLabel>
                </Col>
                <Col lg={10}>
                  <StyledInput
                    value={jobFunction}
                    onChange={(e) => setJobFunction(e.target.value)}
                  />
                  <StyledVisibilityToggle>Only Me</StyledVisibilityToggle>
                </Col>
              </Row>
            </div>
            <Row>
              <Col lg={12}>
                <div className='flex align-items-center justify-content-end mb-3'>
                  {/* <ButtonLink to='/'>Discard changes</ButtonLink> */}

                  <LoadingButton
                    variant='primary'
                    onClick={handleSave}
                    loading={isSaving}
                    loadingText='Saving...'
                  >
                    Save changes
                  </LoadingButton>
                </div>
              </Col>
            </Row>
          </StyledCard>

          {/* <StyledCard className='my-3'>
            <StyledTitle>Survey Questions</StyledTitle>

            <div className='mt-2 mb-3'>
              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel>Question #1</StyledLabel>
                </Col>
                <Col lg={10}>
                  <p className='mb-2'>
                    What are the largest challenges facing your organization?
                  </p>

                  <Editor
                    editorState={EditorState.createEmpty()}
                    toolbarClassName='toolbarClassName'
                    wrapperClassName='wrapperClassName'
                    editorClassName='editorClassName'
                    onEditorStateChange={() => {}}
                  />

                  <StyledVisibilityToggle>Only Me</StyledVisibilityToggle>
                </Col>
              </Row>

              <Row className='py-2'>
                <Col lg={2}>
                  <StyledLabel>Question #2</StyledLabel>
                </Col>
                <Col lg={10}>
                  <p className='mb-2'>How can our platform best serve you?</p>

                  <Editor
                    editorState={EditorState.createEmpty()}
                    toolbarClassName='toolbarClassName'
                    wrapperClassName='wrapperClassName'
                    editorClassName='editorClassName'
                    onEditorStateChange={() => {}}
                  />

                  <StyledVisibilityToggle>Only Me</StyledVisibilityToggle>
                </Col>
              </Row>
            </div>
          </StyledCard> */}

        </Col>
      </Row>
    </div>
  );
};
